import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type BillLine = {
  id?: number
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  expense_account: number | null
  sort_order: number
  subtotal?: string
  tax_amount?: string
  line_total?: string
}

type Bill = {
  id: number
  number: string
  vendor_reference: string
  partner: number
  partner_name?: string
  bill_date: string
  due_date: string | null
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID'
  currency_code: string
  notes: string
  subtotal: string
  tax_total: string
  total: string
  amount_paid: string
  amount_due: string
  lines: BillLine[]
  journal_entry: number | null
  journal_entry_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }

const STATUS_COLOR: Record<Bill['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-blue-100 text-blue-800',
  PAID: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-700',
}

type TabKey = 'details' | 'lines' | 'files'

// ─────────────────── Page ───────────────────
export default function FinanceBillDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const billId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const billQ = useQuery({
    queryKey: ['finance', 'bill', billId],
    queryFn: async () => (await api.get<Bill>(`/finance/bills/${billId}/`)).data,
    enabled: !!billId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Bill>) =>
      (await api.patch(`/finance/bills/${billId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bill', billId] })
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/finance/bills/${billId}/`),
    onSuccess: () => {
      toast.success('Bill deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
      navigate('/admin/finance/bills')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const postMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/finance/bills/${billId}/post_to_gl/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bill', billId] })
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
      toast.success('Posted to General Ledger')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Quick-pay drawer state
  const [payOpen, setPayOpen] = useState(false)
  const [payBankAccount, setPayBankAccount] = useState<number | null>(null)

  const payMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/finance/bills/${billId}/quick_pay/`, {
        bank_account_id: payBankAccount,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'bill', billId] })
      qc.invalidateQueries({ queryKey: ['finance', 'bills'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
      toast.success('Payment recorded')
      setPayOpen(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (billQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (billQ.isError || !billQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Bill not found.</p>
          <Link to="/admin/finance/bills" className="text-blue-600 hover:underline">← Back to bills</Link>
        </div>
      </AdminLayout>
    )
  }

  const bill = billQ.data
  const vendors = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'VENDOR' || p.partner_type === 'BOTH')
  )
  const expenseAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && (a.account_type === 'EXPENSE' || a.account_type === 'COST_OF_SALES')
  )
  const bankAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'ASSET' && a.code.startsWith('11')
  )
  const locked = bill.journal_entry != null
  const canPay = bill.status === 'OPEN' && bankAccounts.length > 0

  const save = (payload: Partial<Bill>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  function openPay() {
    setPayBankAccount(bankAccounts[0]?.id ?? null)
    setPayOpen(true)
  }

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/finance/bills" className="text-muted-foreground hover:text-foreground hover:underline">← Bills</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">
              {bill.number}
              {bill.vendor_reference && <span> · ref {bill.vendor_reference}</span>}
            </div>
            <h2 className="text-2xl font-bold text-foreground">
              {bill.partner_name || <span className="italic">No vendor</span>}
            </h2>
            <div className="text-sm text-muted-foreground">
              <span>Billed {bill.bill_date}</span>
              {bill.due_date && <span> · Due {bill.due_date}</span>}
              <span> · </span>
              <span className="font-mono">{bill.currency_code} {Number(bill.total).toFixed(2)}</span>
              {Number(bill.amount_paid) > 0 && (
                <span> · Paid <span className="font-mono">{Number(bill.amount_paid).toFixed(2)}</span></span>
              )}
              {Number(bill.amount_due) > 0 && bill.status !== 'PAID' && (
                <span> · Due <span className="font-mono">{Number(bill.amount_due).toFixed(2)}</span></span>
              )}
              {bill.journal_entry_number && (
                <span> · Posted as <span className="font-mono">{bill.journal_entry_number}</span></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GenerateDocumentButton
              sourceType="finance.Bill"
              sourceId={bill.id}
              label="Generate document…"
            />
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[bill.status]}`}>{bill.status}</span>
            ) : (
              <InlineStatus
                value={bill.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'OPEN', label: 'Open' },
                  { value: 'PAID', label: 'Paid' },
                  { value: 'VOID', label: 'Void' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {bill.status === 'DRAFT' && !locked && (
                  <DropdownMenuItem
                    onClick={() => confirm(
                      `Post bill ${bill.number} to General Ledger (creates a journal entry and locks line items)`,
                      () => postMut.mutate(),
                    )}
                    disabled={postMut.isPending}
                    className="text-green-700"
                  >
                    Post to General Ledger
                  </DropdownMenuItem>
                )}
                {canPay && (
                  <DropdownMenuItem
                    onClick={openPay}
                    className="text-purple-700"
                  >
                    Quick pay…
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={locked}
                  onClick={() => !locked && confirm(
                    `bill ${bill.number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!locked ? 'text-destructive' : ''}
                >
                  {locked ? 'Delete (posted to GL)' : 'Delete bill'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'lines', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'lines' && `Line items (${bill.lines.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          bill={bill}
          vendors={vendors}
          locked={locked}
          onSave={save}
        />
      )}
      {tab === 'lines' && (
        <LinesTab
          bill={bill}
          expenseAccounts={expenseAccounts}
          locked={locked}
          onSaveLines={(newLines) => save({ lines: newLines } as Partial<Bill>)}
        />
      )}
      {tab === 'files' && <FilesTab parent="finance.bill" parentId={billId} />}

      {/* Quick-pay drawer */}
      <RecordDrawer
        open={payOpen}
        onOpenChange={(v) => { if (!v) setPayOpen(false) }}
        title="Quick pay"
        description={`Record a full payment for this bill from a bank account. Creates a payment record and posts to the General Ledger.`}
        onSubmit={() => { if (payBankAccount) payMut.mutate() }}
        isPending={payMut.isPending}
        submitLabel="Record payment"
      >
        <div className="bg-muted/40 rounded p-3 mb-4 text-sm">
          <div className="font-medium">{bill.partner_name}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Bill <span className="font-mono">{bill.number}</span>
            {bill.vendor_reference && <span> · ref {bill.vendor_reference}</span>}
          </div>
          <div className="text-base font-mono font-semibold mt-2">
            {bill.currency_code} {Number(bill.amount_due).toFixed(2)}
          </div>
        </div>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="pay-bank">Pay from bank account *</FieldLabel>
            <SearchableSelect
              id="pay-bank"
              value={payBankAccount ? String(payBankAccount) : undefined}
              onChange={(v) => setPayBankAccount(Number(v))}
              placeholder="— select —"
              options={bankAccounts.map((a) => ({
                value: String(a.id), label: a.name, hint: a.code,
              }))}
            />
            <FieldDescription>
              Only ASSET accounts with codes starting with 11 appear here (cash / bank accounts).
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  bill, vendors, locked, onSave,
}: {
  bill: Bill
  vendors: Partner[]
  locked: boolean
  onSave: (payload: Partial<Bill>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This bill is <strong>posted to the General Ledger</strong> ({bill.journal_entry_number}) and read-only.
          Reverse the journal entry to edit, or VOID and reissue.
        </div>
      )}

      <Section title="Bill header">
        <Row label="Number">
          <InlineText
            value={bill.number}
            onSave={(v) => onSave({ number: v })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Vendor reference">
          <InlineText
            value={bill.vendor_reference}
            onSave={(v) => onSave({ vendor_reference: v })}
            disabled={locked}
            placeholder="Vendor's own bill number"
            className="font-mono"
          />
        </Row>
        <Row label="Vendor">
          <InlineSelect
            value={String(bill.partner)}
            displayLabel={bill.partner_name || <Dash />}
            options={vendors.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            disabled={locked}
          />
        </Row>
        <Row label="Bill date">
          <InlineDate
            value={bill.bill_date}
            onSave={(v) => onSave({ bill_date: v || bill.bill_date })}
          />
        </Row>
        <Row label="Due date">
          <InlineDate value={bill.due_date} onSave={(v) => onSave({ due_date: v })} />
        </Row>
        <Row label="Currency">
          <InlineText
            value={bill.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Status">
          {locked ? (
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[bill.status]}`}>{bill.status}</span>
          ) : (
            <InlineStatus
              value={bill.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'OPEN', label: 'Open' },
                { value: 'PAID', label: 'Paid' },
                { value: 'VOID', label: 'Void' },
              ]}
              onSave={(v) => onSave({ status: v })}
            />
          )}
        </Row>
      </Section>

      <Section title="Totals">
        <Row label="Subtotal">
          <span className="font-mono">{Number(bill.subtotal).toFixed(2)} {bill.currency_code}</span>
        </Row>
        <Row label="Tax">
          <span className="font-mono">{Number(bill.tax_total).toFixed(2)} {bill.currency_code}</span>
        </Row>
        <Row label="Total">
          <span className="font-mono font-bold text-base">
            {Number(bill.total).toFixed(2)} {bill.currency_code}
          </span>
        </Row>
        <Row label="Paid">
          <span className="font-mono">{Number(bill.amount_paid).toFixed(2)} {bill.currency_code}</span>
        </Row>
        <Row label="Outstanding">
          <span className={`font-mono ${Number(bill.amount_due) > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
            {Number(bill.amount_due).toFixed(2)} {bill.currency_code}
          </span>
        </Row>
        {bill.journal_entry_number && (
          <Row label="GL entry">
            <Link to="/admin/finance/journal-entries"
              className="text-blue-600 hover:underline font-mono text-sm">
              {bill.journal_entry_number}
            </Link>
          </Row>
        )}
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={bill.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            disabled={locked}
            placeholder="Internal notes on this bill"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Line items tab ───────────────────

type LineDraft = {
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  expense_account: number | null
}

function emptyLineDraft(expenseAccounts: Account[]): LineDraft {
  return {
    description: '',
    quantity: '1',
    unit_price: '0',
    tax_rate: '0',
    expense_account: expenseAccounts[0]?.id ?? null,
  }
}

function LinesTab({
  bill, expenseAccounts, locked, onSaveLines,
}: {
  bill: Bill
  expenseAccounts: Account[]
  locked: boolean
  onSaveLines: (lines: BillLine[]) => Promise<void>
}) {
  const { confirm, dialog } = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<LineDraft>(emptyLineDraft(expenseAccounts))
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (creating) setDraft(emptyLineDraft(expenseAccounts))
  }, [creating, expenseAccounts])

  function openCreate() {
    setDraft(emptyLineDraft(expenseAccounts))
    setCreating(true)
  }

  async function saveLineField(idx: number, patch: Partial<BillLine>) {
    const next = bill.lines.map((l, i) => i === idx ? { ...l, ...patch } : l)
    await onSaveLines(serializeLines(next))
  }

  async function addLine() {
    setPending(true)
    try {
      const next: BillLine[] = [
        ...bill.lines,
        {
          description: draft.description,
          quantity: draft.quantity,
          unit_price: draft.unit_price,
          tax_rate: draft.tax_rate,
          expense_account: draft.expense_account,
          sort_order: bill.lines.length,
        },
      ]
      await onSaveLines(serializeLines(next))
      toast.success('Line added')
      setCreating(false)
    } catch (e) {
      toast.error(extractErr(e))
    } finally {
      setPending(false)
    }
  }

  async function removeLine(idx: number) {
    const next = bill.lines.filter((_, i) => i !== idx)
    try {
      await onSaveLines(serializeLines(next))
      toast.success('Line removed')
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  // Live footer totals.
  let subtotal = 0, taxTotal = 0
  bill.lines.forEach((l) => {
    const s = Number(l.quantity) * Number(l.unit_price)
    const t = s * Number(l.tax_rate) / 100
    subtotal += s; taxTotal += t
  })

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          {locked
            ? 'Bill posted — line items are read-only. Reverse the journal entry to edit.'
            : 'Edit quantities, unit prices, and tax rates inline. Totals recompute on save.'}
        </p>
        <Button size="sm" onClick={openCreate} disabled={locked}>
          <Plus className="h-3 w-3 mr-1" /> Add line
        </Button>
      </div>

      {bill.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">No line items yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Description</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit price</Th>
                <Th className="text-right">Tax %</Th>
                <Th>Expense account</Th>
                <Th className="text-right">Line total</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {bill.lines.map((l, i) => {
                const s = Number(l.quantity) * Number(l.unit_price)
                const t = s * Number(l.tax_rate) / 100
                const acct = expenseAccounts.find((a) => a.id === l.expense_account)
                return (
                  <tr key={l.id ?? i}>
                    <Td className="font-medium">
                      <InlineText
                        value={l.description}
                        onSave={(v) => saveLineField(i, { description: v })}
                        disabled={locked}
                        placeholder="Item description"
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.quantity}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { quantity: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.unit_price}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { unit_price: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.tax_rate}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { tax_rate: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}%</span>}
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.expense_account ? String(l.expense_account) : ''}
                        displayLabel={acct ? `${acct.code} ${acct.name}` : <Dash />}
                        allowClear
                        options={expenseAccounts.map((a) => ({
                          value: String(a.id),
                          label: `${a.code} ${a.name}`,
                          searchText: `${a.code} ${a.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { expense_account: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {(s + t).toFixed(2)}
                    </Td>
                    <Td className="text-right">
                      {!locked && (
                        <RowActions
                          onEdit={() => {/* inline edits cover the whole row */}}
                          onDelete={() => confirm(
                            `line "${l.description || '(blank)'}"`,
                            () => removeLine(i),
                          )}
                        />
                      )}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-muted/40 text-sm">
              <tr>
                <td colSpan={5} className="px-4 py-2 text-right font-medium text-muted-foreground">Subtotal</td>
                <td className="px-4 py-2 text-right font-mono">{subtotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={5} className="px-4 py-2 text-right font-medium text-muted-foreground">Tax</td>
                <td className="px-4 py-2 text-right font-mono">{taxTotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr className="font-bold">
                <td colSpan={5} className="px-4 py-2 text-right text-foreground">Total</td>
                <td className="px-4 py-2 text-right font-mono">{bill.currency_code} {(subtotal + taxTotal).toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add line item"
        description="Add an expense line to this bill."
        onSubmit={addLine}
        isPending={pending}
        submitLabel="Add line"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="li-desc">Description *</FieldLabel>
            <Textarea id="li-desc" autoFocus required value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={2} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="li-qty">Quantity</FieldLabel>
              <Input id="li-qty" type="number" step="0.01" value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-price">Unit price</FieldLabel>
              <Input id="li-price" type="number" step="0.01" value={draft.unit_price}
                onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-tax">Tax %</FieldLabel>
              <Input id="li-tax" type="number" step="0.01" value={draft.tax_rate}
                onChange={(e) => setDraft({ ...draft, tax_rate: e.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="li-acct">Expense account</FieldLabel>
            <SearchableSelect
              id="li-acct"
              value={draft.expense_account ? String(draft.expense_account) : NONE}
              onChange={(v) => setDraft({ ...draft, expense_account: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...expenseAccounts.map((a) => ({
                  value: String(a.id), label: a.name, hint: a.code,
                })),
              ]}
            />
            <FieldDescription>Drives the debit account on the GL posting.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// Strip computed line fields (subtotal/tax_amount/line_total) the backend
// rejects, re-stamp sort_order, and return a clean payload-ready array.
function serializeLines(lines: BillLine[]): BillLine[] {
  return lines.map((l, i) => ({
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
    tax_rate: l.tax_rate,
    expense_account: l.expense_account,
    sort_order: i,
  }))
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
